import type * as React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { AuditAction, AuditTargetType } from '@etiquetachef/types';
import { AuditFilters } from '@/features/audit/AuditFilters';
import { AuditList } from '@/features/audit/AuditList';

/**
 * Tela `/audit` — trilha global de auditoria (todas as empresas), com filtro
 * por ação e por alvo (tipo + id).
 *
 * Origem: sem issue — commit 05030f0d1, 2026-04-26 (Sprint 2, aba Audit do
 * tenant + página `/audit` global).
 *
 * Contrato: monta os filtros e entrega a `AuditList`, que assina
 * `subscribeAuditEvents` (`lib/firestoreAudit.ts`). O alvo só entra no filtro
 * com tipo E id preenchidos (id sozinho é ignorado aqui; a aba do tenant usa
 * o filtro só por `targetId`). Só leitura.
 *
 * Usado por: rota `audit` (`routes/router.tsx`), menu Sistema
 * (`AdminLayout`). A auditoria financeira tem tela própria (`/audit/billing`,
 * `AuditBillingPage`, spec `docs/screens/audit-billing.md`).
 *
 * Dados: coleção raiz `/audit/{eventId}` (schema `AuditEventSchema` de
 * `@etiquetachef/types`), ordenada por `at desc`; a rule `match
 * /audit/{eventId}` só deixa super-admin ler. Índices compostos em
 * `firebase/firestore.indexes.json`: `action+at`, `targetType+targetId+at`,
 * `targetId+at`, `actorUserId+at`.
 *
 * Dicas: ação + alvo juntos não têm índice composto próprio — a consulta
 * depende de o Firestore combinar `targetType+targetId+at` com `action+at`.
 * Se um dia vier `failed-precondition` nessa combinação, o índice
 * `targetType+targetId+action+at` é o que falta.
 */
export function AuditPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [action, setAction] = useState<AuditAction | null>(null);
  const [targetType, setTargetType] = useState<AuditTargetType | null>(null);
  const [targetId, setTargetId] = useState('');

  const filters = useMemo(() => {
    const out: { action?: AuditAction; targetType?: AuditTargetType; targetId?: string } = {};
    if (action) out.action = action;
    if (targetType && targetId.trim().length > 0) {
      out.targetType = targetType;
      out.targetId = targetId.trim();
    }
    return out;
  }, [action, targetType, targetId]);

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('audit.subtitle')}>{t('audit.title')}</Header>}
    >
      <SpaceBetween size="l">
        <AuditFilters
          action={action}
          targetType={targetType}
          targetId={targetId}
          onChangeAction={setAction}
          onChangeTargetType={setTargetType}
          onChangeTargetId={setTargetId}
        />
        <AuditList filters={filters} />
      </SpaceBetween>
    </ContentLayout>
  );
}
